/**
 * i18n Configuration
 * Centralizes locale settings for the application
 */

export const locales = ["en", "ar", "fr", "de"] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = "en";

/**
 * Full locale codes for cookie storage
 */
export const fullLocaleCodes: Record<Locale, string> = {
  en: "en_US",
  ar: "ar_001",
  de: "de_DE",
  fr: "fr_FR"
};

/**
 * Short codes for display (2-letter)
 */
export const shortLocaleCodes: Record<Locale, string> = {
  en: "EN",
  ar: "AR",
  de: "DE",
  fr: "FR"
};

/**
 * Native language labels for the language switcher UI
 */
export const localeLabels: Record<Locale, string> = {
  en: "English (US)",
  ar: "العربية",
  fr: "Français",
  de: "Deutsch"
};

/**
 * RTL languages
 */
export const rtlLocales: Locale[] = ["ar"];

/**
 * Check if a locale uses RTL (Right-to-Left) text direction
 * @param locale - The locale code to check
 * @returns True if the locale uses RTL, false otherwise
 */
export const isRtlLocale = (locale: string): boolean => {
  return rtlLocales.includes(locale as Locale);
};

/**
 * Check if a string is a valid supported locale
 * @param locale - The string to validate
 * @returns True if the locale is supported, false otherwise
 */
export const isValidLocale = (locale: string): locale is Locale => {
  return locales.includes(locale as Locale);
};

/**
 * Get the full locale code for a given short locale
 * @param locale - The short locale code (e.g., "en", "fr")
 * @returns The full locale code (e.g., "en_US", "fr_FR")
 */
export const getFullLocaleCode = (locale: Locale): string => {
  return fullLocaleCodes[locale];
};

/**
 * Get the display label for a locale in its native language
 * @param locale - The locale code
 * @returns The native language label for the locale
 */
export const getLocaleLabel = (locale: Locale): string => {
  return localeLabels[locale];
};
